import { Component } from '@theme/component';

/*
 * Dual-handle price range slider.
 * Ported from goat-shopify-redesign's filters.js, adapted to Horizon's
 * Component base + declarative on:input + facets-form-component integration.
 */
class PriceRangeComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this._rangeMin = parseFloat(this.dataset.min || '0');
    this._rangeMax = parseFloat(this.dataset.max || '100');
    this._minGap = 1; // currency units — keep thumbs from overlapping

    // Which thumb the shopper is dragging has to be inferred from the values, because
    // `event.target` cannot say: Horizon's declarative `on:input` delegation resolves the
    // handler element with `closest('[on\\:input]')` and, when that is not the original target,
    // hands the handler a Proxy whose `target` is THIS component (assets/component.js). So the
    // previous `event.target === minInput` tests never matched and the thumbs were free to
    // cross, submitting `filter.v.price.gte=50&lte=0` and returning nothing.
    this._lastMin = parseFloat(this.refs.minInput?.value ?? this._rangeMin);
    this._lastMax = parseFloat(this.refs.maxInput?.value ?? this._rangeMax);

    // Stash the original `name` attributes (e.g. filter.v.price.gte/lte) so we
    // can detach/restore them based on whether the slider is at its extremes.
    // Without this, the form would submit the default min/max as an "active"
    // price filter even when the user hasn't touched the slider — clicking any
    // OTHER facet (e.g. a size pill) would then incorrectly bake the price
    // range into the URL as if the user had applied a price filter.
    if (this.refs.minInput) this._minName = this.refs.minInput.getAttribute('name');
    if (this.refs.maxInput) this._maxName = this.refs.maxInput.getAttribute('name');

    const controller = new AbortController();
    this._abortController = controller;
    const { signal } = controller;

    const handleRelease = () => {
      const facetsForm = this.closest('facets-form-component');
      if (facetsForm && typeof facetsForm.updateFilters === 'function') {
        facetsForm.updateFilters();
      }
    };

    if (this.refs.minInput) {
      this.refs.minInput.addEventListener('mouseup', handleRelease, { signal });
      this.refs.minInput.addEventListener('touchend', handleRelease, { signal });
    }

    if (this.refs.maxInput) {
      this.refs.maxInput.addEventListener('mouseup', handleRelease, { signal });
      this.refs.maxInput.addEventListener('touchend', handleRelease, { signal });
    }

    this._syncNameAttributes();
    this._updateFill();
    this._updateLabels();

    // The facets morph patches this subtree from server HTML after every filter
    // update, and the server always renders `name` on the range inputs — which
    // would make the NEXT facet click submit a phantom price filter (gte=0&lte=max).
    // Re-run the sync whenever the morph touches the inputs. _syncNameAttributes
    // only mutates when out of sync, so this can't loop.
    this._morphObserver = new MutationObserver(() => this._syncNameAttributes());
    this._morphObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['name', 'value'],
    });
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._morphObserver?.disconnect();
    if (typeof super.disconnectedCallback === 'function') {
      super.disconnectedCallback();
    }
  }

  /**
   * Called by the morph after a facets update patches this element in place.
   * The server HTML always renders `name` on the range inputs, so the morph
   * restores them — re-strip when the slider sits at its defaults, otherwise
   * the NEXT facet click submits a phantom price filter (gte=0&lte=max).
   */
  updatedCallback() {
    if (typeof super.updatedCallback === 'function') {
      super.updatedCallback();
    }
    this._rangeMin = parseFloat(this.dataset.min || '0');
    this._rangeMax = parseFloat(this.dataset.max || '100');
    this._syncNameAttributes();
    this._updateFill();
    this._updateLabels();
  }

  handleInput() {
    const { minInput, maxInput } = this.refs;
    if (!minInput || !maxInput) return;

    const minValue = parseFloat(minInput.value);
    const maxValue = parseFloat(maxInput.value);

    if (maxValue - minValue <= this._minGap) {
      // Push the stationary thumb, never the one under the pointer, so the drag keeps tracking.
      if (maxValue !== this._lastMax) {
        maxInput.value = String(Math.min(this._rangeMax, minValue + this._minGap));
      } else {
        minInput.value = String(Math.max(this._rangeMin, maxValue - this._minGap));
      }
    }

    this._lastMin = parseFloat(minInput.value);
    this._lastMax = parseFloat(maxInput.value);

    this._syncNameAttributes();
    this._updateFill();
    this._updateLabels();
  }

  /**
   * Toggle the `name` attribute on each range input based on whether its value
   * equals the slider's range extreme. Inputs without a `name` aren't submitted
   * with the form — so when the slider sits at its defaults, no price filter
   * param ends up in the URL even if another facet (e.g. a size pill) triggers
   * a form submission.
   */
  _syncNameAttributes() {
    const { minInput, maxInput } = this.refs;
    if (!minInput || !maxInput) return;

    const minAtFloor = parseFloat(minInput.value) <= this._rangeMin;
    const maxAtCeiling = parseFloat(maxInput.value) >= this._rangeMax;

    // Only mutate when out of sync — the mutation observer above watches these
    // attributes, so unconditional writes would ping-pong.
    if (minAtFloor) {
      if (minInput.hasAttribute('name')) minInput.removeAttribute('name');
    } else if (this._minName && minInput.getAttribute('name') !== this._minName) {
      minInput.setAttribute('name', this._minName);
    }

    if (maxAtCeiling) {
      if (maxInput.hasAttribute('name')) maxInput.removeAttribute('name');
    } else if (this._maxName && maxInput.getAttribute('name') !== this._maxName) {
      maxInput.setAttribute('name', this._maxName);
    }
  }

  _updateFill() {
    const { fill, minInput, maxInput } = this.refs;
    if (!fill || !minInput || !maxInput) return;

    const range = this._rangeMax - this._rangeMin;
    if (range === 0) return;

    const minValue = parseFloat(minInput.value);
    const maxValue = parseFloat(maxInput.value);

    const minPosition = Math.max(0, Math.min(100, ((minValue - this._rangeMin) / range) * 100));
    const maxPosition = Math.max(0, Math.min(100, ((maxValue - this._rangeMin) / range) * 100));

    const clampedLeft = Math.min(minPosition, maxPosition);
    const clampedRight = Math.max(0, 100 - Math.max(minPosition, maxPosition));

    fill.style.left = clampedLeft + '%';
    fill.style.right = clampedRight + '%';

    // The label row is inset to the same span, so `space-between` keeps each label under its thumb.
    const { labels } = this.refs;
    if (labels) {
      labels.style.marginInlineStart = clampedLeft + '%';
      labels.style.marginInlineEnd = clampedRight + '%';
    }
  }

  _updateLabels() {
    const { minLabel, maxLabel, minInput, maxInput } = this.refs;
    if (!minLabel || !maxLabel || !minInput || !maxInput) return;

    // Printed as-is, not sorted for display: `handleInput` above makes the handles unable to
    // cross, so sorting here could only ever mask a regression — it is what let a reversed range
    // read as a tidy "$0.00 - $50.00" while the form still submitted gte=50&lte=0.
    minLabel.textContent = parseFloat(minInput.value).toFixed(2);
    maxLabel.textContent = parseFloat(maxInput.value).toFixed(2);
  }
}

customElements.define('price-range-component', PriceRangeComponent);
